import { useRef, useState } from "react";
import { PinGate } from "./components/PinGate";
import { ArchivePasswordGate } from "./components/ArchivePasswordGate";
import { DeploymentLauncher } from "./components/DeploymentLauncher";
import { BP26ArchiefView } from "./components/BP26ArchiefView";
import { US25ArchiefView } from "./components/US25ArchiefView";
import { TopBar } from "./components/TopBar";
import { EccTimeline } from "./components/EccTimeline";
import { StagesNow } from "./components/StagesNow";
import { DocsHub } from "./components/docs/DocsHub";
import { Inzet } from "./components/Inzet";
import { LogboekPM26 } from "./components/LogboekPM26";
import { US26ArchiefView } from "./components/US26ArchiefView";
import { Timeline } from "./components/Timeline";
import { InstroomWidget } from "./components/InstroomWidget";
import { RapportView } from "./components/RapportView";
import { CrisisWizard } from "./components/CrisisWizard";
import { WeatherTile } from "./components/WeatherTile";
import { useFirestoreCollection } from "./hooks/useFirestoreCollection";
import type { ChecklistEntry, IncidentEntry } from "./lib/types";
import { Section } from "./components/Section";
import { DesktopShell } from "./components/desktop/DesktopShell";
import { LiveAnnouncer } from "./components/LiveAnnouncer";
import { OfflineBanner } from "./components/OfflineBanner";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { ecc, showDayInzet } from "./lib/data";

const TABS = [
  { id: "logboek", label: "Logboek" },
  { id: "ecc", label: "ECC" },
  { id: "docs", label: "Bronnen" },
  { id: "inzet", label: "Inzet" },
] as const;

type Tab = (typeof TABS)[number]["id"];

function lock() {
  try {
    localStorage.removeItem("pm26.pin.ok");
  } catch {
    /* ignore */
  }
  location.reload();
}

export default function App() {
  return (
    <PinGate>
      <Shell />
    </PinGate>
  );
}

type View = "launcher" | "cp" | "bp26-archief" | "us25-archief" | "us26-archief" | "rapport";

function Shell() {
  const [view, setView] = useState<View>("cp");
  const [crisisOpen, setCrisisOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  if (view === "launcher") {
    return (
      <DeploymentLauncher
        onOpenBP26Archief={() => setView("bp26-archief")}
        onOpenUS25Archief={() => setView("us25-archief")}
        onOpenUS26Archief={() => setView("us26-archief")}
        onOpenCP={() => setView("cp")}
        onLock={lock}
      />
    );
  }

  if (view === "rapport") {
    return <RapportHost onClose={() => setView("cp")} />;
  }

  if (view === "bp26-archief") {
    return (
      <ArchivePasswordGate onCancel={() => setView("launcher")}>
        <BP26ArchiefView onBack={() => setView("launcher")} />
      </ArchivePasswordGate>
    );
  }

  if (view === "us26-archief") {
    return (
      <ArchivePasswordGate onCancel={() => setView("launcher")}>
        <US26ArchiefView onBack={() => setView("launcher")} />
      </ArchivePasswordGate>
    );
  }

  if (view === "us25-archief") {
    return (
      <ArchivePasswordGate onCancel={() => setView("launcher")}>
        <US25ArchiefView onBack={() => setView("launcher")} />
      </ArchivePasswordGate>
    );
  }

  // view === "cp": active Pal Mundo CP — no second password
  const toOverzicht = () => setView("launcher");

  return (
    <>
      <a href="#main" className="skip-link">
        Sla navigatie over
      </a>
      <LiveAnnouncer />
      <OfflineBanner variant={isDesktop ? "desktop" : "mobile"} />
      <UpdatePrompt />
      {isDesktop ? (
        <DesktopShell
          onLock={toOverzicht}
          onOpenRapport={() => setView("rapport")}
          onOpenBommelding={() => setCrisisOpen(true)}
        />
      ) : (
        <MobileShell
          onBack={toOverzicht}
          onOpenRapport={() => setView("rapport")}
          onOpenBommelding={() => setCrisisOpen(true)}
        />
      )}
      <CrisisWizard open={crisisOpen} onClose={() => setCrisisOpen(false)} />
    </>
  );
}

function MobileShell({
  onBack,
  onOpenRapport,
  onOpenBommelding,
}: {
  onBack: () => void;
  onOpenRapport: () => void;
  onOpenBommelding: () => void;
}) {
  const [tab, setTab] = useState<Tab>("logboek");
  const { data: incidentData } = useFirestoreCollection<IncidentEntry>(
    "incidents",
  );

  return (
    <div className="min-h-full pb-24">
      <TopBar onLock={onBack} />
      <main id="main" className="px-3 pt-2 max-w-2xl mx-auto">
        <h1 className="sr-only">Pal Mundo Control Panel</h1>
        <div role="region" aria-label={TABS.find((t) => t.id === tab)?.label}>
          {tab === "logboek" && (
            <Section title="Logboek" accent="default">
              <LogboekPM26 />
              <WeatherTile />
              <InstroomWidget incidents={incidentData} />
              {/* Live meldingen-feed — zelfde Timeline als desktop (kolom 1) */}
              <Timeline onOpenBommelding={onOpenBommelding} />
            </Section>
          )}
          {tab === "ecc" && (
            <>
              <Section title="Podia nu — tik voor dagprogramma" count="5" accent="ok">
                <StagesNow />
              </Section>
              <Section title="ECC tijdlijn" count={`${ecc.length}`} accent="warn">
                <EccTimeline />
              </Section>
            </>
          )}
          {tab === "docs" && (
            <Section title="Bronnen" count="1 document" accent="default">
              <DocsHub />
              <button
                onClick={onOpenRapport}
                className="w-full card p-3 mt-3 flex items-center gap-2 text-zinc-300 active:bg-ink-700 hover:bg-ink-700/40 transition-colors min-h-[44px]"
              >
                <span>Rapport — meldingen en instroom (afdrukbaar)</span>
              </button>
            </Section>
          )}
          {tab === "inzet" && (
            <Section
              title="Inzet beveiliging"
              count={`${showDayInzet.length} posities`}
            >
              <Inzet />
            </Section>
          )}
        </div>
      </main>
      <TabBar tab={tab} onChange={setTab} />
    </div>
  );
}

function TabBar({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  const tabsRef = useRef<HTMLDivElement | null>(null);

  function handleKeyDown(e: React.KeyboardEvent, idx: number) {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : -1;
    const next = (idx + dir + TABS.length) % TABS.length;
    onChange(TABS[next].id);
    const nextBtn = tabsRef.current?.querySelectorAll<HTMLButtonElement>(
      '[role="tab"]',
    )[next];
    nextBtn?.focus();
  }

  return (
    <nav
      aria-label="Hoofdsecties"
      className="fixed bottom-0 left-0 right-0 z-30 safe-bottom"
    >
      <div className="absolute inset-0 bg-ink-950/85 backdrop-blur-xl pointer-events-none" />
      <div
        className="absolute inset-x-0 top-0 h-px pointer-events-none"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(255,255,255,0.06) 20%, rgba(255,255,255,0.06) 80%, transparent)",
        }}
      />
      <div
        ref={tabsRef}
        role="tablist"
        aria-orientation="horizontal"
        className="relative max-w-2xl mx-auto grid grid-cols-4"
      >
        {TABS.map((t, i) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              aria-current={active ? "page" : undefined}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(t.id)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className={`py-3 min-h-[52px] text-2xs font-medium transition-colors no-scale ${
                active ? "text-accent" : "text-zinc-500 active:text-zinc-200"
              }`}
            >
              <div
                className={`w-1 h-1 rounded-full mx-auto mb-1 transition-all duration-300 ease-spring ${
                  active ? "bg-accent scale-100" : "bg-transparent scale-75"
                }`}
              />
              {t.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}


function RapportHost({ onClose }: { onClose: () => void }) {
  const { data: incidents } = useFirestoreCollection<IncidentEntry>(
    "incidents",
    { orderField: "time", orderDir: "asc" },
  );
  const { data: checklist } = useFirestoreCollection<ChecklistEntry>(
    "checklist",
  );
  return (
    <RapportView
      incidents={incidents}
      checklistState={checklist}
      onClose={onClose}
    />
  );
}
